import { jwtDecode } from 'jwt-decode';
export function isWoltUserType(userType) {
    return userType.includes('wolt');
}
export function isGuestUserType(userType) {
    return userType.includes('guest');
}
export const parseUser = (accessToken, userType, 
// eslint-disable-next-line @typescript-eslint/no-unused-vars
_) => {
    const tokenData = jwtDecode(accessToken);
    if ('user' in tokenData && isWoltUserType(userType)) {
        return tokenData.user;
    }
    if ('guest_user' in tokenData && isGuestUserType(userType)) {
        return tokenData.guest_user;
    }
    throw new Error(`Missing expected user field in access token payload. Expected user type: ${userType.map((t) => `"${t}"`).join('|')}`);
};
export const createAccessTokenData = ({ access_token, refresh_token, expires_in }, now = Date.now()) => {
    return {
        accessToken: access_token,
        refreshToken: refresh_token,
        accessTokenExpiresAt: Math.floor(now / 1000) * 1000 + expires_in * 1000,
    };
};
export function accessTokenDataIsValid(accessTokenData) {
    return (typeof accessTokenData.accessToken === 'string' &&
        typeof accessTokenData.accessTokenExpiresAt === 'number' &&
        (typeof accessTokenData.refreshToken === 'string' ||
            typeof accessTokenData.refreshToken === 'undefined'));
}
export function accessTokenIsExpired({ accessTokenExpiresAt }, { expirationOffset }) {
    return Date.now() + expirationOffset >= accessTokenExpiresAt;
}
export function deepEqual(obj1, obj2) {
    if (obj1 === obj2) {
        return true;
    }
    return JSON.stringify(obj1) === JSON.stringify(obj2);
}
/** Compares two arrays as sets ignoring the order of the items. */
function asSetEqual(a, b) {
    if (a.length !== b.length) {
        return false;
    }
    const set = new Set(b);
    return a.every((item) => set.has(item));
}
/**
 * Tells if two user objects represent the same data.
 *
 * The JSON representation of the user data in the JWT token isn't guaranteed by Wolt Authentication to be stable between token refreshes.
 * The order of items in arrays (roles, permissions), as well as the order of entries in the user object can change
 * even without an underlying data change.
 * This function takes these variations into account when performing a comparison.
 */
export function userEqual(value1, value2) {
    if (value1 === value2) {
        return true;
    }
    const keysValue1 = Object.keys(value1);
    const keysValue2 = Object.keys(value2);
    const keysAreEqual = asSetEqual(keysValue1, keysValue2);
    if (!keysAreEqual) {
        return false;
    }
    return Object.entries(value1).every(([key, value]) => {
        const otherValue = Reflect.get(value2, key);
        if (!value || !otherValue) {
            return value === otherValue;
        }
        if (Array.isArray(value) && Array.isArray(otherValue)) {
            return asSetEqual(value, otherValue);
        }
        if (typeof value === 'object' &&
            typeof otherValue === 'object' &&
            !Array.isArray(value) &&
            !Array.isArray(otherValue)) {
            return userEqual(value, otherValue);
        }
        return value === otherValue;
    });
}
export function coerceToError(err) {
    return err instanceof Error
        ? err
        : err && typeof err === 'object' && 'toString' in err
            ? new Error(err.toString())
            : new Error(`Unknown error: "${err}"`);
}
function random(min, max) {
    return Math.floor(Math.random() * (max - min + 1) + min);
}
function wait(delay) {
    return new Promise((resolve) => setTimeout(resolve, delay));
}
/**
 * Retries calling `fn` if it rejects.
 * The retries are delayed with an exponential backoff starting with `baseDelayMillis` until `fn` has been retried calling `maxRetries` times.
 * The delay value for each retry is randomised. The last retry will be delayed for at max `maxDelayMillis`.
 *
 * @see https://aws.amazon.com/blogs/architecture/exponential-backoff-and-jitter/
 */
export async function retryOnRejection(fn, { maxRetries = 10, baseDelayMillis: baseDelay = 300, maxDelayMillis: maxDelay = 5000, } = {}) {
    let attempt = 0;
    let delay = baseDelay;
    let error;
    const exponent = (maxDelay / baseDelay) ** (1 / (maxRetries - 1));
    while (attempt <= maxRetries) {
        if (attempt > 0) {
            const randomizedDelay = random(0, delay);
            await wait(randomizedDelay);
        }
        try {
            return await fn();
        }
        catch (_error) {
            error = _error;
            delay = Math.round(baseDelay * exponent ** attempt);
            attempt++;
        }
    }
    throw error;
}
export function snakeCaseKeys(object) {
    return Object.fromEntries(Object.entries(object).map(([key, value]) => [snakeCase(key), value]));
}
function snakeCase(camelCase) {
    return camelCase.replace(/[a-z][A-Z]/g, (match) => `${match[0]}_${match[1].toLowerCase()}`);
}
